import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const [blogsRes, featuredRes] = await Promise.all([
          axios.get(`${API}/blogs`),
          axios.get(`${API}/blogs/featured`)
        ]);
        setBlogs(blogsRes.data);
        setFeaturedBlogs(featuredRes.data);
      } catch (err) {
        console.error("Error fetching blogs:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  const categories = ["All", ...new Set(blogs.map(blog => blog.category))];
  
  const filteredBlogs = selectedCategory === "All" 
    ? blogs 
    : blogs.filter(blog => blog.category === selectedCategory);

  return (
    <div data-testid="blogs-page" className="page-transition">
      {/* Hero Section */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
              Insights & Perspectives
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none text-slate-900 font-['Outfit'] mb-6">
              Thought Leadership
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed font-['Plus_Jakarta_Sans']">
              Expert insights on technology trends, best practices, and industry perspectives 
              from our team of consultants and engineers.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {!loading && featuredBlogs.length > 0 && (
        <section className="bg-slate-900 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-8">
              Featured Articles
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {featuredBlogs.map((blog, index) => (
                <Link 
                  key={blog.id}
                  to={`/blogs/${blog.slug}`}
                  data-testid={`featured-blog-${index}`}
                  className="group bg-slate-800 border border-slate-700 hover:border-blue-500 transition-colors"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img 
                      src={blog.image_url}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-bold uppercase tracking-wider text-blue-400">
                        {blog.category}
                      </span>
                      <span className="text-slate-600">|</span>
                      <span className="flex items-center text-xs text-slate-400">
                        <Clock className="w-3 h-3 mr-1" />
                        {blog.read_time}
                      </span>
                    </div>
                    <h2 className="text-xl font-semibold text-white font-['Outfit'] mb-2 group-hover:text-blue-400 transition-colors">
                      {blog.title}
                    </h2>
                    <p className="text-slate-400 text-sm line-clamp-2 font-['Plus_Jakarta_Sans']">
                      {blog.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap items-center gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? "bg-blue-700 text-white"
                    : "bg-white text-slate-600 border border-slate-200 hover:border-blue-600"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
              <p className="mt-4 text-slate-600">Loading articles...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBlogs.map((blog, index) => (
                <Link 
                  key={blog.id}
                  to={`/blogs/${blog.slug}`}
                  data-testid={`blog-card-${index}`}
                  className="group bg-white border border-slate-200 hover:border-blue-600 transition-colors"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img 
                      src={blog.image_url}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-bold uppercase tracking-wider text-blue-600">
                        {blog.category}
                      </span>
                      <span className="flex items-center text-xs text-slate-400">
                        <Clock className="w-3 h-3 mr-1" />
                        {blog.read_time}
                      </span>
                    </div>
                    <h2 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-2 group-hover:text-blue-700 transition-colors line-clamp-2">
                      {blog.title}
                    </h2>
                    <p className="text-slate-600 text-sm line-clamp-2 mb-4 font-['Plus_Jakarta_Sans']">
                      {blog.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="text-sm">
                        <span className="text-slate-900 font-medium">{blog.author}</span>
                        <span className="text-slate-400 block text-xs mt-1">{blog.author_role}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
          
          {!loading && filteredBlogs.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-600">No articles found in this category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
